import { ClearinghouseState, OpenOrder, SpotBalance, SpotClearinghouseState, UserPortfolio } from '@/api/hyperliquid';
import { WsOrder } from '@/lib/hyperliquid/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface AssetBalance {
  symbol: string;
  name: string;
  balance: number;
  value: number;
  price: number;
  change24h: number;
  allocation: number;
  type: 'perp' | 'spot';
  pnl?: number;
  pnlPercent?: number;
}

interface Transaction {
  date: string;
  pair: string;
  type: 'Buy' | 'Sell';
  amount: number;
  price: number;
  total: number;
  status: 'Completed';
}

interface PortfolioState {
  // Tab states
  activeTab: 'twap' | 'tradeHistory' | 'fundingHistory' | 'orderHistory' | 'deposits';
  timeframe: 'all' | 'perps' | 'spot' | 'evm';

  // Loading states
  loading: boolean;

  perpClearinghouseState: ClearinghouseState | null;
  spotClearinghouseState: SpotClearinghouseState | null;
  userPortfolio: Array<[string, UserPortfolio]>;
  userOpenOrders: OpenOrder[];

  // Data states
  perpPositions: ClearinghouseState | null;
  spotBalances: SpotClearinghouseState | null;
  assets: AssetBalance[];
  transactions: Transaction[];

  // Balance states
  totalBalance: number;
  totalChange: number;
  changePercent: number;
  availableBalance: number;
  usdcPerps: number;
  spotBalance: number;
  evmBalance: number;

  // Modal states
  showConnectionModal: boolean;
  showTransferModal: boolean;
  showEVMTransferModal: boolean;
  showSendModal: boolean;

  maxBuilderFee: number;

  // Actions
  setActiveTab: (tab: 'twap' | 'tradeHistory' | 'fundingHistory' | 'orderHistory' | 'deposits') => void;
  setTimeframe: (timeframe: 'all' | 'perps' | 'spot' | 'evm') => void;
  setLoading: (loading: boolean) => void;
  setPerpPositions: (positions: ClearinghouseState | null) => void;
  setSpotBalances: (balances: SpotClearinghouseState | null) => void;
  setAssets: (assets: AssetBalance[]) => void;
  setTransactions: (transactions: Transaction[]) => void;

  setPerpClearinghouseState: (state: ClearinghouseState) => void;
  setSpotClearinghouseState: (state: SpotClearinghouseState) => void;
  setSpotClearinghouseBalances: (balances: SpotBalance[]) => void;
  setUserPortfolio: (portfolio: Array<[string, UserPortfolio]>) => void;
  setUserOpenOrders: (openOrders: OpenOrder[]) => void;
  addUserOpenOrders: (openOrders: OpenOrder[]) => void;
  updateUserOpenOrders: (openOrders: WsOrder[]) => void;

  // Balance actions
  setTotalBalance: (balance: number) => void;
  setAvailableBalance: (balance: number) => void;
  setUsdcPerps: (balance: number) => void;
  setSpotBalance: (balance: number) => void;
  setMaxBuilderFee: (fee: number) => void;

  // Modal actions
  setShowConnectionModal: (show: boolean) => void;
  setShowTransferModal: (show: boolean) => void;
  setShowEVMTransferModal: (show: boolean) => void;
  setShowSendModal: (show: boolean) => void;

  // Combined update action for efficiency
  updateBalances: (data: {
    totalBalance?: number;
    availableBalance?: number;
    usdcPerps?: number;
    spotBalance?: number;
  }) => void;
}

const usePortfolioStore = create<PortfolioState>()(
  devtools(
    set => ({
      // Initial states
      activeTab: 'deposits',
      timeframe: 'all',
      loading: true,
      perpPositions: null,
      spotBalances: null,
      assets: [],
      transactions: [],
      perpClearinghouseState: null,
      spotClearinghouseState: null,
      userPortfolio: [],
      userOpenOrders: [],

      // Balances
      totalBalance: 0,
      totalChange: -22.86,
      changePercent: -2.296,
      availableBalance: 0,
      usdcPerps: 0,
      spotBalance: 0,
      evmBalance: 0.0,

      // Modals
      showConnectionModal: false,
      showTransferModal: false,
      showEVMTransferModal: false,
      showSendModal: false,
      maxBuilderFee: 0,

      // Actions
      setActiveTab: tab => set({ activeTab: tab }),
      setTimeframe: timeframe => set({ timeframe }),
      setLoading: loading => set({ loading }),
      setPerpPositions: positions => set({ perpPositions: positions }),
      setSpotBalances: balances => set({ spotBalances: balances }),
      setAssets: assets => set({ assets }),
      setTransactions: transactions => set({ transactions }),
      setPerpClearinghouseState: state => set({ perpClearinghouseState: state }),
      setSpotClearinghouseState: state => set({ spotClearinghouseState: state }),
      setMaxBuilderFee: fee => set({ maxBuilderFee: fee }),
      setSpotClearinghouseBalances: balances =>
        set(state => {
          const newState = { ...state };
          newState.spotClearinghouseState.balances = balances;
          return newState;
        }),
      setUserPortfolio: portfolio => set({ userPortfolio: portfolio }),
      setUserOpenOrders: openOrders => set({ userOpenOrders: openOrders }),
      addUserOpenOrders: (openOrders: OpenOrder[]) =>
        set(state => {
          let updatedOrders = [...state.userOpenOrders];

          console.log('Adding open orders:', openOrders);

          openOrders.forEach(newOrder => {
            const existingIndex = updatedOrders.findIndex(o => o.oid === newOrder.oid);

            if (existingIndex !== -1) {
              updatedOrders[existingIndex] = newOrder;
            } else {
              updatedOrders = [newOrder].concat(updatedOrders);
            }
          });

          return { userOpenOrders: updatedOrders };
        }),
      updateUserOpenOrders: (wsOrders: WsOrder[]) =>
        set(state => {
          // 기존 주문들 복사
          let updatedOrders = [...state.userOpenOrders];

          wsOrders.forEach(wsOrder => {
            const orderData: OpenOrder = {
              coin: wsOrder.order.coin,
              limitPx: wsOrder.order.limitPx,
              oid: wsOrder.order.oid,
              side: wsOrder.order.side,
              sz: wsOrder.order.sz,
              origSz: wsOrder.order.origSz,
              cloid: wsOrder.order.cloid,
              timestamp: wsOrder.statusTimestamp,
            };

            // 같은 oid를 가진 기존 주문의 인덱스 찾기
            const existingIndex = updatedOrders.findIndex(o => o.oid === wsOrder.order.oid);

            if (wsOrder.status === 'open') {
              // open 상태: 있으면 업데이트, 없으면 추가
              if (existingIndex !== -1) {
                updatedOrders[existingIndex] = orderData;
              } else {
                updatedOrders = [orderData].concat(updatedOrders);
              }
            } else if (wsOrder.status === 'canceled' || wsOrder.status === 'filled') {
              // canceled나 filled 상태: 목록에서 삭제
              if (existingIndex !== -1) {
                updatedOrders.splice(existingIndex, 1);
              }
            }
          });

          return { userOpenOrders: updatedOrders };
        }),
      // Balance actions
      setTotalBalance: balance => set({ totalBalance: balance }),
      setAvailableBalance: balance => set({ availableBalance: balance }),
      setUsdcPerps: balance => set({ usdcPerps: balance }),
      setSpotBalance: balance => set({ spotBalance: balance }),

      // Modal actions
      setShowConnectionModal: show => set({ showConnectionModal: show }),
      setShowTransferModal: show => set({ showTransferModal: show }),
      setShowEVMTransferModal: show => set({ showEVMTransferModal: show }),
      setShowSendModal: show => set({ showSendModal: show }),

      // Combined update
      updateBalances: data =>
        set(state => ({
          ...state,
          ...data,
        })),
    }),
    {
      name: 'portfolio-store',
    },
  ),
);

export default usePortfolioStore;
